import {
  createRootRoute,
  createRoute,
  createRouter,
  Link,
  Outlet,
  RouterProvider,
  useNavigate,
} from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { type Operator, session } from './api/client';
import { LanguageSwitch } from './components/language-switch';
import { AcceptInvitePage } from './pages/accept-invite';
import { EventsPage } from './pages/events';
import { LoginPage } from './pages/login';
import { LotSetupPage } from './pages/lot-setup';
import { OrdersPage } from './pages/orders';
import { PromotionsPage } from './pages/promotions';
import { RatesPage } from './pages/rates';
import { RevenuePage } from './pages/revenue';
import { SimulatorPage } from './pages/simulator';
import { TeamPage } from './pages/team';
import { hasPermission, type OperatorPermission } from './permissions';
import { SelectedLotProvider, useSelectedLot } from './selected-lot';

const NAV = [
  { path: '/', key: 'rates', permission: 'rates.manage' },
  { path: '/simulator', key: 'simulator', permission: 'rates.manage' },
  { path: '/events', key: 'events', permission: 'events.manage' },
  { path: '/lot', key: 'lot', permission: 'lots.manage' },
  { path: '/orders', key: 'orders', anyPermission: ['orders.manage', 'refunds.manage'] },
  { path: '/revenue', key: 'revenue', permission: 'revenue.view' },
  { path: '/promotions', key: 'promotions', permission: 'promotions.manage' },
  { path: '/team', key: 'team', ownerOnly: true },
] as const;

function canAccessNav(operator: Operator, item: (typeof NAV)[number]) {
  if ('ownerOnly' in item) return operator.role === 'owner';
  if ('permission' in item) return hasPermission(operator, item.permission as OperatorPermission);
  return item.anyPermission.some((permission) => hasPermission(operator, permission));
}

export function defaultRouteForOperator(operator: Operator) {
  return NAV.find((item) => canAccessNav(operator, item))?.path ?? '/login';
}

function Shell() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const operator = session.operator;
  const { lots, loaded, lotId, select } = useSelectedLot();
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    if (!operator) void navigate({ to: '/login' });
  }, [navigate]);

  useEffect(() => {
    if (operator?.role === 'owner' && loaded && lots.length === 0) {
      void navigate({ to: '/lot' });
    }
  }, [loaded, lots.length, navigate]);

  if (!operator) return null;

  const current = lots.find((lot) => lot.id === lotId);
  const isOwner = operator.role === 'owner';

  /**
   * One definition, two placements: a permanent rail on a desk and a slide-over
   * on a phone. Duplicating the markup would guarantee the two drift apart.
   */
  const sidebar = (
    <>
      <div className="rounded-xl border border-hairline bg-white p-3">
        <span className="text-[10px] tracking-wider text-faint">{t('shell.currentLot')}</span>
        <select
          className="mt-1 w-full bg-transparent text-sm font-bold outline-none"
          value={lotId ?? ''}
          onChange={(event) => select(event.target.value)}
        >
          {lots.map((lot) => (
            <option key={lot.id} value={lot.id}>
              {lot.name}
            </option>
          ))}
        </select>
        <span className="num text-[11px] text-muted">
          {t('shell.lotCount', { code: current?.code ?? '—', count: lots.length })}
        </span>
      </div>

      <nav className="flex flex-col gap-0.5">
        {NAV.filter((item) => canAccessNav(operator, item)).map((item) => (
          <Link
            key={item.path}
            to={item.path}
            onClick={() => setMenuOpen(false)}
            className="rounded-lg px-3 py-3 hover:bg-[#efeee9] lg:py-2"
            activeProps={{ className: 'rounded-lg bg-ink px-3 py-3 text-white hover:bg-ink lg:py-2' }}
            activeOptions={{ exact: item.path === '/' }}
          >
            <span className="block text-[13.5px] font-semibold">{t(`nav.${item.key}`)}</span>
          </Link>
        ))}
      </nav>

      <div className="mt-auto flex flex-col gap-2 border-t border-hairline pt-3">
        <LanguageSwitch />
        <div className="flex items-center gap-2 text-xs">
          <span
            className={`h-2 w-2 rounded-full ${current?.status === 'online' ? 'bg-success' : 'bg-warning'}`}
          />
          <span>{t(current?.status === 'online' ? 'shell.online' : 'shell.offline')}</span>
        </div>
        <p className="text-[11px] leading-relaxed text-muted">{t('shell.offlineNote')}</p>
        <div className="flex items-center justify-between text-[11px]">
          <span className="text-muted">
            {operator.name} · {t(isOwner ? 'shell.owner' : 'shell.staff')}
          </span>
          <button
            type="button"
            className="text-accent"
            onClick={() => {
              session.clear();
              void navigate({ to: '/login' });
            }}
          >
            {t('shell.signOut')}
          </button>
        </div>
      </div>
    </>
  );

  return (
    <div className="flex min-h-full flex-col lg:flex-row">
      {/* Phone chrome. The lot name is here because "which lot am I editing"
          is the one thing that must never be behind a menu. */}
      <header className="sticky top-0 z-20 flex items-center gap-3 border-b border-hairline bg-surface-shell px-3 py-2 lg:hidden">
        <button
          type="button"
          aria-label={t('shell.menu')}
          aria-expanded={menuOpen}
          onClick={() => setMenuOpen(true)}
          className="flex h-10 w-10 items-center justify-center rounded-lg border border-hairline text-lg"
        >
          ☰
        </button>
        <span className="flex-1 truncate text-sm font-bold">{current?.name ?? '—'}</span>
        <LanguageSwitch />
      </header>

      {menuOpen ? (
        <div className="fixed inset-0 z-30 lg:hidden">
          <button
            type="button"
            aria-label={t('shell.closeMenu')}
            className="absolute inset-0 bg-ink/40"
            onClick={() => setMenuOpen(false)}
          />
          <aside className="relative flex h-full w-64 max-w-[82%] flex-col gap-4 overflow-y-auto border-r border-hairline bg-surface-shell p-3">
            {sidebar}
          </aside>
        </div>
      ) : null}

      <aside className="hidden w-56 shrink-0 flex-col gap-4 border-r border-hairline bg-surface-shell p-3 lg:flex">
        {sidebar}
      </aside>

      <main className="min-w-0 flex-1 overflow-x-hidden p-4 lg:p-6">
        {loaded ? <Outlet /> : <p className="text-sm text-muted">{t('shell.loadingLots')}</p>}
      </main>
    </div>
  );
}

/** Reads the selected lot for a page; pages never guess it themselves. */
function useLotContext() {
  const { lotId } = useSelectedLot();
  const operator = session.operator;
  return {
    lotId: lotId ?? '',
    can: (permission: OperatorPermission) => hasPermission(operator, permission),
    isOwner: operator?.role === 'owner',
  };
}

const rootRoute = createRootRoute({ component: Outlet });

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  component: function Login() {
    const navigate = useNavigate();
    return (
      <LoginPage
        onSignedIn={() => {
          const operator = session.operator;
          void navigate({ to: operator ? defaultRouteForOperator(operator) : '/login' });
        }}
      />
    );
  },
});

const acceptInviteRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/accept-invite',
  component: function AcceptInvite() {
    const navigate = useNavigate();
    return (
      <AcceptInvitePage onAccepted={(operator) => void navigate({ to: defaultRouteForOperator(operator) })} />
    );
  },
});

const shellRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'shell',
  component: function SelectedLotShell() {
    return (
      <SelectedLotProvider>
        <Shell />
      </SelectedLotProvider>
    );
  },
});

function pageComponent(
  render: (ctx: {
    lotId: string;
    can: (permission: OperatorPermission) => boolean;
    isOwner: boolean;
  }) => React.ReactNode,
) {
  return function Page() {
    return <>{render(useLotContext())}</>;
  };
}

const ratesRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/',
  component: pageComponent((ctx) => <RatesPage lotId={ctx.lotId} canEdit={ctx.can('rates.manage')} />),
});
const simulatorRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/simulator',
  component: pageComponent((ctx) => <SimulatorPage lotId={ctx.lotId} />),
});
const eventsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/events',
  component: pageComponent((ctx) => <EventsPage canEdit={ctx.can('events.manage')} />),
});
const lotRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/lot',
  component: pageComponent((ctx) => <LotSetupPage lotId={ctx.lotId} canEdit={ctx.can('lots.manage')} />),
});
const ordersRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/orders',
  component: pageComponent((ctx) => (
    <OrdersPage lotId={ctx.lotId} canEdit={ctx.can('orders.manage')} canRefund={ctx.can('refunds.manage')} />
  )),
});
const revenueRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/revenue',
  component: pageComponent(() => <RevenuePage />),
});
const promotionsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/promotions',
  component: pageComponent((ctx) => <PromotionsPage canEdit={ctx.can('promotions.manage')} />),
});
const teamRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/team',
  component: pageComponent((ctx) => <TeamPage canEdit={ctx.isOwner} />),
});

const routeTree = rootRoute.addChildren([
  loginRoute,
  acceptInviteRoute,
  shellRoute.addChildren([
    ratesRoute,
    simulatorRoute,
    eventsRoute,
    lotRoute,
    ordersRoute,
    revenueRoute,
    promotionsRoute,
    teamRoute,
  ]),
]);

export const router = createRouter({ routeTree, defaultPreload: 'intent' });

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

export function App() {
  return <RouterProvider router={router} />;
}
